import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Spin, message } from "antd";
import { createSprSotr, fetchSprSotr, saveSprSotr } from "./spr-sotr-api";
import SprSotrLaws from "./spr-sotr-laws";
import SprSotrMain from "./spr-sotr-main";
import { flagsToLawString, lawStringToFlags } from "./spr-sotr-laws-data";

export const SOTR_TAB_KEYS = {
  MAIN: "main",
  LAWS: "laws"
};

export const SOTR_TAB_ITEMS = [
  { key: SOTR_TAB_KEYS.MAIN, label: "Основная" },
  { key: SOTR_TAB_KEYS.LAWS, label: "Права" }
];

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toFlag = (value) => Number(value) === 1;

const normalizeCard = (row) => ({
  idSotr: toNumber(pick(row, "idSotr", "IdSotr")),
  name: pick(row, "name", "Name") ?? "",
  fullName: pick(row, "fullName", "FullName") ?? "",
  dolj: pick(row, "dolj", "Dolj") ?? "",
  komment: pick(row, "komment", "Komment") ?? "",
  law: pick(row, "law", "Law") ?? "",
  isVrach: toFlag(pick(row, "isVrach", "IsVrach")),
  crmPhone: pick(row, "crmPhone", "CrmPhone") ?? "",
  crmUser: pick(row, "crmUser", "CrmUser") ?? "",
  isDel: toFlag(pick(row, "isDel", "IsDel")),
  kkmPass: pick(row, "kkmPass", "KkmPass") ?? "",
  userLevel: toNumber(pick(row, "userLevel", "UserLevel")),
  inn: pick(row, "inn", "Inn") ?? "",
  snils: pick(row, "snils", "Snils") ?? "",
  phone: pick(row, "phone", "Phone") ?? "",
  onlineKey: pick(row, "onlineKey", "OnlineKey") ?? "",
  positionId: toNumber(pick(row, "positionId", "PositionId")),
  specId: toNumber(pick(row, "specId", "SpecId")),
  signId: pick(row, "signId", "SignId") ?? ""
});

const cardToFormValues = (card) => ({
  idSotr: card.idSotr,
  name: card.name,
  fullName: card.fullName,
  dolj: card.dolj,
  komment: card.komment,
  isVrach: Boolean(card.isVrach),
  crmPhone: card.crmPhone,
  crmUser: card.crmUser,
  isDel: Boolean(card.isDel),
  kkmPass: card.kkmPass,
  userLevel: card.userLevel,
  inn: card.inn,
  snils: card.snils,
  phone: card.phone,
  onlineKey: card.onlineKey,
  positionId: card.positionId,
  specId: card.specId,
  signId: card.signId
});

const emptyCard = () => ({
  idSotr: null,
  name: "",
  fullName: "",
  dolj: "",
  komment: "",
  law: "",
  isVrach: false,
  crmPhone: "",
  crmUser: "",
  isDel: false,
  kkmPass: "",
  userLevel: null,
  inn: "",
  snils: "",
  phone: "",
  onlineKey: "",
  positionId: null,
  specId: null,
  signId: ""
});

/**
 * Карточка сотрудника: вкладки Основная / Права + сохранение.
 */
const SprSotrCard = ({ idSotr, isNew = false, open, activeTab, onSaved, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lawFlags, setLawFlags] = useState(() => lawStringToFlags(""));

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      const card = emptyCard();
      setError("");
      setLoading(false);
      setLawFlags(lawStringToFlags(""));
      form.setFieldsValue(cardToFormValues(card));
      return () => {
        cancelled = true;
      };
    }

    if (idSotr == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprSotr(idSotr)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeCard(row);
        setLawFlags(lawStringToFlags(card.law));
        form.setFieldsValue(cardToFormValues(card));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить сотрудника");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idSotr, form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError("");
      const isDel = values.isDel ? 1 : 0;
      const body = {
        idSotr: isNew ? undefined : idSotr,
        name: (values.name || "").trim(),
        fullName: (values.fullName || "").trim() || null,
        dolj: (values.dolj || "").trim() || null,
        komment: (values.komment || "").trim() || null,
        law: flagsToLawString(lawFlags),
        isVrach: isDel ? 0 : values.isVrach ? 1 : 0,
        crmPhone: (values.crmPhone || "").trim() || null,
        crmUser: (values.crmUser || "").trim() || null,
        isDel,
        kkmPass: (values.kkmPass || "").trim() || null,
        userLevel: values.userLevel ?? null,
        inn: (values.inn || "").trim() || null,
        snils: (values.snils || "").trim() || null,
        phone: (values.phone || "").trim() || null,
        onlineKey: (values.onlineKey || "").trim() || null,
        positionId: values.positionId ?? null,
        specId: values.specId ?? null,
        signId: (values.signId || "").trim() || null
      };

      const saved = isNew ? await createSprSotr(body) : await saveSprSotr(body);
      message.success("Сохранено");
      onSaved?.(saved);
    } catch (e) {
      if (e?.errorFields) return;
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-sotr-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-sotr-card__tab spr-sotr-card__tab--root">
      {error ? <Alert className="spr-sotr-card__error" type="error" showIcon message={error} /> : null}
      <div className="spr-sotr-card__content">
        {activeTab === SOTR_TAB_KEYS.LAWS ? (
          <SprSotrLaws flags={lawFlags} onChange={setLawFlags} disabled={saving} />
        ) : (
          <SprSotrMain form={form} isNew={isNew} disabled={saving} />
        )}
      </div>
      <div className="spr-sotr-card__footer">
        <Button type="primary" loading={saving} onClick={onSave}>
          Сохранить
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprSotrCard;
